import { object, string, array } from "yup";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import { OBJECTIVE_ACQUISITION_STATUS } from "@academy-context/read/domain/types/training-follow-up";
import type {
  ObjectiveDetailVM,
  ObjectiveRating,
  TrainingItemAppraisalVM,
  TrainingItemEvaluationPayload,
} from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { sortObjectivesByNumber } from "@academy-context/read/domain/types/shared/training-item-evaluation";
import type { Nullable } from "@shared-kernel/core/types/nullable";

// Le BROUILLON : tant que le conseiller n'a pas cliqué, un objectif n'a pas de statut. C'est la
// seule forme où l'absence est légitime — la soumission, elle, est complète par construction.
type ObjectiveRatingDraft = {
  objectiveId: string;
  acquisitionStatus: OBJECTIVE_ACQUISITION_STATUS | undefined;
};

export type FormInputs = {
  objectives: ObjectiveRatingDraft[];
  disclosedAppraisal: string;
  privateAppraisal: string;
};

// Une soumission couvre TOUT le groupe : chaque objectif servi doit porter un statut, et
// l'appréciation communiquée à l'élève est obligatoire — sans elle, le conseiller ne lui laisserait
// rien.
export const schema = object().shape({
  objectives: array()
    .of(
      object().shape({
        objectiveId: string().required(),
        acquisitionStatus: string().oneOf(Object.values(OBJECTIVE_ACQUISITION_STATUS)).required(REQUIRED_FIELD),
      }),
    )
    .min(1)
    .required(),
  disclosedAppraisal: string().required(REQUIRED_FIELD),
  privateAppraisal: string(),
});

// Une évaluation déjà rendue revient préremplie : ses statuts, et l'appréciation déposée.
// Re-soumettre écrase, il n'y a pas d'état de réouverture à demander.
export const buildFormValues = (objectives: ObjectiveDetailVM[], appraisal: Nullable<TrainingItemAppraisalVM>): FormInputs => ({
  objectives: sortObjectivesByNumber(objectives).map(objective => ({
    objectiveId: objective.id,
    acquisitionStatus: objective.acquisitionStatus ?? undefined,
  })),
  disclosedAppraisal: appraisal?.disclosedAppraisal ?? "",
  privateAppraisal: appraisal?.privateAppraisal ?? "",
});

const isRated = (draft: ObjectiveRatingDraft): draft is ObjectiveRating => draft.acquisitionStatus !== undefined;

// Le passage du brouillon à la soumission. Le schéma garantit déjà qu'aucun objectif n'est sans
// statut ; ce garde-fou le redit au typage plutôt que par un cast, et refuse bruyamment plutôt que
// d'amputer silencieusement le groupe si la validation venait à être contournée.
export const toEvaluationPayload = (form: FormInputs): TrainingItemEvaluationPayload => {
  const objectives = form.objectives.filter(isRated);
  if (objectives.length !== form.objectives.length) {
    throw new Error("Soumission incomplète : chaque objectif du groupe doit porter un statut d'acquisition.");
  }

  return {
    objectives,
    disclosedAppraisal: form.disclosedAppraisal,
    privateAppraisal: form.privateAppraisal || null,
  };
};
